import {
  Alert,
  Button,
  CheckBox,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Typography,
} from '@execonline-inc/execonline-ui';
import { assertNever } from '@kofno/piper';
import clsx from 'clsx';
import { just } from 'maybeasy';
import { observer } from 'mobx-react';
import * as React from 'react';
import { T, TPlainTextKey } from '../../../../../../Translations';
import Loader from '../../../../../Loader';
import CollaboratorAccessStore from '../Store';
import { EditingState, OpenState } from '../Store/Types';
import { isDraftValid, PermissionType } from '../Types';

interface Props {
  state: OpenState;
  store: CollaboratorAccessStore;
  collectionName: string;
}

// Option labels are server data (shared with the Builder v1 Share step);
// the one-line explanations under them are ours.
const descriptionFor = (permissionType: PermissionType): TPlainTextKey => {
  switch (permissionType) {
    case 'organization':
      return 'All admins in your organization can see and edit this collection.';
    case 'private':
      return 'Only you can see and edit this collection.';
    case 'client-groups':
      return 'Only members of the groups you select can see and edit this collection.';
    default:
      return assertNever(permissionType);
  }
};

// Left-justified title row, matching the style guide's dialogs and the
// Builder v2 ConfirmationModal. The close button comes from Modal itself.
function TitleRow() {
  return (
    <ModalHeader className="w-full items-center">
      <Typography variant="subtitleLarge" fontWeight="semibold">
        <T kind="Manage Collaborator Access" />
      </Typography>
    </ModalHeader>
  );
}

interface FormProps {
  state: EditingState;
  store: CollaboratorAccessStore;
  collectionName: string;
}

const isSaving = (state: EditingState): boolean => {
  switch (state.kind) {
    case 'saving':
      return true;
    case 'ready':
    case 'save-failed':
      return false;
    default:
      return assertNever(state);
  }
};

const showsSaveError = (state: EditingState): boolean => {
  switch (state.kind) {
    case 'save-failed':
      return true;
    case 'ready':
    case 'saving':
      return false;
    default:
      return assertNever(state);
  }
};

// Dismissal is disabled while saving so a stray click cannot drop the
// completion signal.
const isDismissible = (state: OpenState): boolean => {
  switch (state.kind) {
    case 'saving':
      return false;
    case 'loading':
    case 'ready':
    case 'save-failed':
    case 'load-failed':
      return true;
    default:
      return assertNever(state);
  }
};

function AccessForm({ state, store, collectionName }: FormProps) {
  const { options, clientGroups } = state.resource.payload;
  const { draft } = state;
  const saving = isSaving(state);
  const valid = isDraftValid(draft);

  return (
    <>
      <TitleRow />
      <ModalBody className="gap-5">
        <Typography variant="bodyMedium" className="text-muted-foreground">
          <T
            kind="Choose which admins can see and edit <name/>."
            name={<span className="font-semibold text-foreground">{collectionName}</span>}
          />
        </Typography>

        {/* Same native radio treatment as the Share modal on this screen
            (ShareOptionsModal/ShareTypeSelector). */}
        <div role="radiogroup" className="space-y-3">
          {options.map((option) => (
            <label
              key={option.value}
              className={clsx('flex items-start', saving && 'cursor-not-allowed opacity-50')}
              data-intercom-target={`collaborator-access-option-${option.value}`}
            >
              <input
                type="radio"
                name="permission_type"
                value={option.value}
                checked={option.value === draft.permissionType}
                disabled={saving}
                onChange={() => store.selectPermissionType(option.value)}
                className="mt-1 mr-3 text-primary"
              />
              <div>
                <Typography variant="bodyMedium" fontWeight="semibold">
                  {option.label}
                </Typography>
                <Typography variant="bodySmall" className="text-muted-foreground">
                  <T kind={descriptionFor(option.value)} />
                </Typography>
              </div>
            </label>
          ))}
        </div>

        {draft.permissionType === 'client-groups' && (
          <div
            className="max-h-48 overflow-y-auto rounded-md border border-border p-3"
            data-intercom-target="collaborator-access-groups"
          >
            {clientGroups.map((group) => (
              <label key={group.id} className="flex cursor-pointer items-center gap-3 py-1.5">
                <CheckBox
                  checked={draft.clientGroupIds.includes(group.id)}
                  disabled={saving}
                  onChange={() => store.toggleClientGroup(group.id)}
                />
                <Typography variant="bodyMedium">
                  {group.name} <span className="text-muted-foreground">({group.membersCount})</span>
                </Typography>
              </label>
            ))}
            {!valid && (
              <Typography variant="bodySmall" className="mt-2 text-destructive">
                <T kind="Select at least one group." />
              </Typography>
            )}
          </div>
        )}

        {showsSaveError(state) && (
          <Alert status="error" variant="outlined" icon radius="md">
            <T kind="Unable to update collaborator access. Please try again later." />
          </Alert>
        )}
      </ModalBody>
      <ModalFooter className="flex-row justify-end">
        <Button
          variant="text"
          color="secondary"
          disabled={saving}
          onClick={store.close}
          data-intercom-target="collaborator-access-cancel"
        >
          <T kind="Cancel" />
        </Button>
        <Button
          className="border-accent bg-accent text-accent-foreground hover:border-accent/90 hover:bg-accent/90"
          disabled={!valid || saving}
          isLoading={saving}
          onClick={store.save}
          data-intercom-target="collaborator-access-save"
        >
          <T kind="Save" />
        </Button>
      </ModalFooter>
    </>
  );
}

const content = ({ state, store, collectionName }: Props): React.ReactNode => {
  switch (state.kind) {
    case 'loading':
      return (
        <>
          <TitleRow />
          <ModalBody className="items-center">
            <Loader
              type="spin"
              text={just<TPlainTextKey>('Loading...')}
              className="py-8"
              height={40}
              width={40}
              delay={0}
            />
          </ModalBody>
        </>
      );
    case 'ready':
    case 'saving':
    case 'save-failed':
      return <AccessForm state={state} store={store} collectionName={collectionName} />;
    case 'load-failed':
      return (
        <>
          <TitleRow />
          <ModalBody>
            <Alert status="error" variant="outlined" icon radius="md">
              <T kind="Unable to load collaborator access. Please try again later." />
            </Alert>
          </ModalBody>
          <ModalFooter className="flex-row justify-end">
            <Button
              variant="outlined"
              color="secondary"
              onClick={store.close}
              data-intercom-target="collaborator-access-load-failed-close"
            >
              <T kind="Close" />
            </Button>
          </ModalFooter>
        </>
      );
    default:
      return assertNever(state);
  }
};

// One Modal instance hosts every state so the loading -> form switch swaps
// content in place. A successful save closes without a confirmation.
function CollaboratorAccessModal(props: Props) {
  const dismissible = isDismissible(props.state);

  return (
    <Modal
      open
      size="lg"
      escapeKey={dismissible}
      outsidePress={dismissible}
      onClose={dismissible ? props.store.close : undefined}
      classNames={{ closeButton: dismissible ? '' : 'hidden' }}
    >
      {content(props)}
    </Modal>
  );
}

export default observer(CollaboratorAccessModal);
